import { NewsList } from "@/components/News/NewsList";
import { TeamImage } from "@/components/common/Images";
import { NoItems } from "@/components/common/NoItems";
import { News } from "@/models/types";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { DocumentLink } from "@/components/common/DocumentLink";
import { FiChevronRight } from "react-icons/fi";

import { Footer } from "@/components/Footer";
import { Nav } from "@/components/common/FrontendNav";
import { Option, Select } from "@/components/common/Select";
import { getBundesligaNews } from "@/data/news";
import { getBundesligaTeams } from "@/helper/api";
import prisma from "@/helper/client";
import { getTeamDetailUrl } from "@/helper/urlHelper";
import { LeaguTeamWithSpectatorData } from "@/models/LeagueTeam";
import { Season } from "@/models/Season";
import { useRouter } from "next/router";
import { getActiveSeasons } from "@/data/season";
import { setGhostCacheHeader } from "@/helper/cacheHeaders";

export async function getServerSideProps(context: any) {
  const { res } = context;
  setGhostCacheHeader(res);

  if (!context.query.season) {
    return {
      redirect: {
        destination: "/bundesliga/spectators",
        permanent: false,
      },
    };
  }
  const [start, end] = context.query.season.split("-");
  const season = await prisma.season.findFirst({
    where: { startYear: parseInt(start), endYear: parseInt(end) },
  });

  if (!season) {
    return {
      redirect: {
        destination: "/bundesliga/spectators",
        permanent: false,
      },
    };
  }

  const teams = await getBundesligaTeams(season.id);

  const { news } = await getBundesligaNews({
    locale: context.locale,
  });

  if (teams.length === 0) {
    return {
      props: {
        teams: [],
        news,
        ...(await serverSideTranslations(context.locale)),
      },
    };
  }
  const matches = await prisma.match.findMany({
    where: {
      DBStatus: "Active",
      league: "Bundesliga",
      leagueId: teams[0].leagueId,
      matchState: "Finished",
    },
  });

  const fullTeams: LeaguTeamWithSpectatorData[] = teams.map((team) => {
    const teamMatches = matches.filter((m) => m.homeTeamId === team.id);
    const totalSpecs = teamMatches.reduce(
      (specs, m) => (m.spectators ? specs + m.spectators : specs),
      0,
    );
    return {
      ...team,
      spectators: {
        total: totalSpecs,
        average:
          teamMatches.length === 0
            ? 0
            : Math.round(totalSpecs / teamMatches.length).toFixed(0),
      },
    };
  });

  const seasons = await getActiveSeasons();
  return {
    props: {
      teams: fullTeams.sort((a, b) => b.spectators.total - a.spectators.total),
      news,
      seasons,
      selectedSeason: season,
      ...(await serverSideTranslations(context.locale)),
    },
  };
}

interface PageProps {
  teams: LeaguTeamWithSpectatorData[];
  news: News[];
  seasons: Season[];
  selectedSeason: Season;
}
export default function Page({
  teams,
  news,
  selectedSeason,
  seasons,
}: PageProps) {
  const { t } = useTranslation();
  const { push } = useRouter();
  const selectedSeasonQuery = `${selectedSeason.startYear}-${selectedSeason.endYear}`;
  return (
    <>
      <Head>
        <title>{`${t("Table_Tennis")} Bundesliga - TTBL - ${t(
          "Title_Spectators",
        )}`}</title>
      </Head>
      <div>
        <Nav>
          <Select
            value={selectedSeasonQuery}
            onChange={(e) => push(`/bundesliga/spectators/${e}`)}
            className="white lg:border-[1px] lg:ml-4"
          >
            {seasons.map((s) => (
              <Option
                key={s.id}
                value={`${s.startYear}-${s.endYear}`}
              >{`${s.startYear}-${s.endYear}`}</Option>
            ))}
          </Select>
        </Nav>
        <div className="mx-4 mb-4 lg:hidden">
          <Select
            value={selectedSeasonQuery}
            onChange={(e) => push(`/bundesliga/spectators/${e}`)}
            className="white lg:border-[1px] lg:ml-4"
          >
            {seasons.map((s) => (
              <Option
                key={s.id}
                value={`${s.startYear}-${s.endYear}`}
              >{`${s.startYear}-${s.endYear}`}</Option>
            ))}
          </Select>
        </div>
        <div className="container container-padding-responsive">
          {teams.length === 0 && <NoItems />}
          {teams.length > 0 && (
            <div className="p-4 lg:p-0 bg-white lg:bg-inherit">
              <div className="grid grid-cols-6 text-gray-text text-md lg:px-4">
                <div className="text-center">{t("Label_Rank")}</div>
                <div className="col-span-2">{t("Label_Team")}</div>
                <div className="max-lg:hidden text-center">
                  {t("Spectator_Total")}
                </div>
                <div className="max-lg:hidden text-center">
                  {t("Spectator_Average")}
                </div>
                <div className="lg:hidden text-center">{t("Total")}</div>
                <div className="lg:hidden text-center">{t("Average")}</div>
                <div className="ml-auto" />
              </div>
              {teams.map((team, idx) => (
                <TeamSpectatorCard
                  team={team}
                  key={team.id}
                  rank={idx + 1}
                  season={selectedSeason}
                />
              ))}
            </div>
          )}
        </div>

        {news.length > 0 && (
          <div className="container container_py">
            <NewsList news={news} title="Bundesliga News" type="bundesliga" />
          </div>
        )}
        <Footer />
      </div>
    </>
  );
}

interface TeamSpectatorCardProps {
  team: LeaguTeamWithSpectatorData;
  rank: number;
  season: Season;
}
function TeamSpectatorCard({ team, rank, season }: TeamSpectatorCardProps) {
  return (
    <DocumentLink
      href={getTeamDetailUrl({
        league: "Bundesliga",
        season,
        teamId: team.seasonTeam.id,
      })}
      className="bg-white grid grid-cols-6 items-center border-b border-gray-light group last:border-none py-4 lg:border-none lg:rounded-lg lg:bg-bg-gray lg:p-4 lg:my-4 max-lg:gap-4"
    >
      <div className="text-center">{rank}</div>
      <div className="col-span-2 flex lg:gap-4">
        <TeamImage team={team.seasonTeam} />
        <div className="max-lg:hidden ml-4 whitespace-nowrap">
          {team.seasonTeam.name}
        </div>
        <div className="lg:hidden ml-4 whitespace-nowrap">
          {team.seasonTeam.shortname}
        </div>
      </div>
      <div className="text-center">{team.spectators.total}</div>
      <div className="text-center">{team.spectators.average}</div>
      <div className="ml-auto">
        <FiChevronRight />
      </div>
    </DocumentLink>
  );
}
